// Mirrors core-platform's plan contract (Modules/Billing CreatePlanFormRequest
// e UpdatePlanFormRequest): label 2–25 caracteres, preço mínimo de R$ 6,00 por
// parcela (600 centavos), usd_price obrigatório (mínimo US$ 0,50 = 50 centavos)
// quando a carteira internacional está ativa, e ao menos um método de pagamento.

export const LABEL_MIN_LENGTH = 2;
export const LABEL_MAX_LENGTH = 25;
export const MIN_PRICE_CENTS = 600;
export const MIN_USD_PRICE_CENTS = 50;

// O backend valida em inglês (locale `en`, sem traduções) — cada campo ganha
// aqui uma mensagem no idioma ativo que enuncia a restrição completa daquele
// campo. `t` chega do componente para não congelar o idioma no import.
const fieldMessages = (t) => ({
    label: t("contentModals:managePlan.validation.label"),
    price: t("contentModals:managePlan.validation.price"),
    usd_price: t("contentModals:managePlan.validation.usdPrice"),
    methods: t("contentModals:managePlan.validation.methods"),
    installments: t("contentModals:managePlan.validation.installments"),
    interval: t("contentModals:managePlan.validation.interval"),
    community: t("contentModals:managePlan.validation.community"),
    anticipation: t("contentModals:managePlan.validation.anticipation"),
});

// Campos que possuem um input visível no modal para exibir o erro inline.
const INLINE_FIELDS = ["label", "price", "usd_price", "methods"];

// Campos que o core recusa (422) quando o plano já tem assinantes ativos ou
// expirados. A UI só os envia quando liberados, então esse 422 indica uma
// assinatura feita durante a edição.
const SUBSCRIBER_LOCKED_FIELDS = ["price", "interval", "autorenew", "anticipation"];

export function activeMethodKeys(methods) {
    return Object.keys(methods || {}).filter(key => methods[key]?.enabled);
}

export function validatePlanConfig(info, { t, pricingLocked = false, requiresUsdPrice = false } = {}) {
    const messages = fieldMessages(t);
    const errors = {};

    const label = (info?.label || "").trim();
    if (label.length < LABEL_MIN_LENGTH || label.length > LABEL_MAX_LENGTH) {
        errors.label = messages.label;
    }

    // Preço travado (plano com assinantes) não é enviado — nem validado.
    if (!pricingLocked) {
        const price = parseInt(info?.price, 10) || 0;
        if (price < MIN_PRICE_CENTS) {
            errors.price = t("contentModals:managePlan.validation.minPrice");
        }
    }

    if (requiresUsdPrice) {
        const usdPrice = parseInt(info?.usd_price, 10) || 0;
        if (usdPrice < MIN_USD_PRICE_CENTS) {
            errors.usd_price = messages.usd_price;
        }
    }

    if (activeMethodKeys(info?.methods).length === 0) {
        errors.methods = messages.methods;
    }

    return { isValid: Object.keys(errors).length === 0, errors };
}

// Converte o bag de erros 422 do Laravel ({campo: [mensagens]}) em erros
// inline (campos com input visível) + mensagens gerais para o alerta do modal.
export function mapApiValidationErrors(apiErrors, { t, usdPriceFieldVisible = true } = {}) {
    const messagesByField = fieldMessages(t);
    const fieldErrors = {};
    const generalMessages = [];

    Object.entries(apiErrors || {}).forEach(([key, messages]) => {
        const root = key.split(".")[0];
        const list = Array.isArray(messages) ? messages : [String(messages)];

        if (
            SUBSCRIBER_LOCKED_FIELDS.includes(root)
            && list.some(message => /subscribers/i.test(message))
        ) {
            const locked = t("contentModals:managePlan.validation.pricingLocked");
            if (!generalMessages.includes(locked)) generalMessages.push(locked);
            return;
        }

        if (root === "usd_price" && !usdPriceFieldVisible) {
            // O backend exige usd_price quando há carteira internacional ativa;
            // se o campo não está visível (carteiras ainda não carregadas), o
            // usuário precisa saber o motivo mesmo sem o input na tela.
            generalMessages.push(
                t("contentModals:managePlan.validation.usdPriceHidden")
            );
            return;
        }

        const translated = messagesByField[root];

        if (translated && INLINE_FIELDS.includes(root)) {
            fieldErrors[root] = translated;
            return;
        }

        if (translated) {
            generalMessages.push(translated);
            return;
        }

        list.forEach(message => {
            generalMessages.push(message);
        });
    });

    return { fieldErrors, generalMessages };
}
